import type { ResponseAcademicRisk } from '@/domains/academicRisk/academicRiskStudent.entity';
import type { AcademicRiskStudentRepository } from '@/domains/academicRisk/academicRiskStudent.repository';
import type { FilterVigenteStudentsUseCase } from './filterVigenteStudentsUseCase';
import type { FilterStudentsByCurrentYearAcademicRiskUseCase } from './filterStudentsByCurrentYearAcademicRiskUseCase';

/**
 * Use Case para obtener datos de riesgos académicos desde el repositorio
 *
 * Responsabilidades:
 * - Obtener datos completos de riesgo académico desde la fuente de datos
 * - Aplicar reglas de negocio sobre los datos obtenidos (filtro de vigentes y año actual)
 * - Actuar como intermediario entre la capa de dominio y la infraestructura
 */
export class GetAcademicRiskStudentUseCase {
  private academicRiskRepository: AcademicRiskStudentRepository;
  private filterVigenteUseCase: FilterVigenteStudentsUseCase;
  private filterByCurrentYearUseCase: FilterStudentsByCurrentYearAcademicRiskUseCase;

  /**
   * Inicializa el UseCase con las dependencias necesarias
   * @param academicRiskRepository - Repositorio que implementa la interfaz AcademicRiskRepository
   * @param filterVigenteUseCase - Use Case para filtrar estudiantes vigentes
   * @param filterByCurrentYearUseCase - Use Case para filtrar estudiantes por año actual
   */
  constructor(
    academicRiskRepository: AcademicRiskStudentRepository,
    filterVigenteUseCase: FilterVigenteStudentsUseCase,
    filterByCurrentYearUseCase: FilterStudentsByCurrentYearAcademicRiskUseCase,
  ) {
    this.academicRiskRepository = academicRiskRepository;
    this.filterVigenteUseCase = filterVigenteUseCase;
    this.filterByCurrentYearUseCase = filterByCurrentYearUseCase;
  }

  /**
   * Ejecuta la obtención de datos de riesgo académico
   * Aplica filtro de estudiantes vigentes y filtro por año actual antes de retornar
   * @throws Error si falla la comunicación con el repositorio
   */
  async execute(): Promise<ResponseAcademicRisk> {
    const rawData = await this.academicRiskRepository.getAcademicRiskStudent();
    const onlyVigentes = this.filterVigenteUseCase.execute(rawData);
    return this.filterByCurrentYearUseCase.execute(onlyVigentes);
  }
}
